// SignalsPage — /signals, the full attention queue.
//
// URL-synced filters (?severity=, ?category=) pass straight through to the
// server call; the body groups results by severity in block → attention →
// notice order. Category chips inside a group header narrow to one category;
// an active category shows as a removable token in the filter bar.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchSignals,
  formatWhen,
  SEVERITY_LABEL,
  type Signal,
  type SignalSeverity,
} from '@/lib/commandCenterClient';
import { OcEmpty, OcError, OcLoading, SignalRowView } from '@/components/oc/bits';

const SEVERITY_ORDER: SignalSeverity[] = ['block', 'attention', 'notice'];

const SEVERITY_TABS: Array<{ value: '' | SignalSeverity; label: string }> = [
  { value: '', label: 'All' },
  { value: 'block', label: 'Blocks' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'notice', label: 'Notices' },
];

export function SignalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const severity = searchParams.get('severity') ?? '';
  const category = searchParams.get('category') ?? '';

  const [signals, setSignals] = useState<Signal[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic sequence — a stale response (filters changed mid-flight) is dropped.
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSignals({
        ...(severity ? { severity } : {}),
        ...(category ? { category } : {}),
      });
      if (seq !== seqRef.current) return;
      setSignals(res.signals);
      setGeneratedAt(res.generatedAt);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load signals');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [severity, category]);

  useEffect(() => {
    void load();
    return () => {
      // Invalidate any in-flight request on filter change / unmount.
      seqRef.current++;
    };
  }, [load]);

  const setParam = (key: 'severity' | 'category', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const groups = useMemo(() => {
    const m = new Map<SignalSeverity, Signal[]>();
    for (const s of signals) {
      const list = m.get(s.severity);
      if (list) list.push(s);
      else m.set(s.severity, [s]);
    }
    return m;
  }, [signals]);

  // Label for the active category token — the filtered results all carry it;
  // fall back to the raw key when the filter matched nothing.
  const categoryLabel =
    signals.find((s) => s.category === category)?.categoryLabel ?? category;

  return (
    <div className="oc-page">
      <div className="oc-page-header">
        <h1 className="oc-page-title">Signals</h1>
        {generatedAt && <span className="oc-meta">as of {formatWhen(generatedAt)}</span>}
      </div>

      <div className="oc-filterbar">
        {SEVERITY_TABS.map((tab) => (
          <button
            key={tab.value || 'all'}
            type="button"
            className="oc-view-tab"
            data-active={severity === tab.value ? 'true' : undefined}
            onClick={() => setParam('severity', tab.value)}
          >
            {tab.label}
          </button>
        ))}
        {category && (
          <span className="oc-token">
            category: <b>{categoryLabel}</b>
            <button
              type="button"
              onClick={() => setParam('category', '')}
              aria-label={`Clear category filter ${categoryLabel}`}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {loading && <OcLoading />}
      {!loading && error && (
        <OcError>
          <span>{error}</span>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void load()}
            style={{ marginLeft: 'var(--space-3)' }}
          >
            Retry
          </button>
        </OcError>
      )}

      {!loading && !error && signals.length === 0 && <OcEmpty>Nothing needs attention.</OcEmpty>}

      {!loading &&
        !error &&
        SEVERITY_ORDER.map((sev) => {
          const group = groups.get(sev);
          if (!group || group.length === 0) return null;
          const categories = distinctCategories(group);
          return (
            <section className="oc-panel" key={sev}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  flexWrap: 'wrap',
                  marginBottom: 'var(--space-3)',
                }}
              >
                <span className="oc-kicker">
                  {SEVERITY_LABEL[sev]} · {group.length}
                </span>
                {!category &&
                  categories.length > 1 &&
                  categories.map((c) => (
                    <button
                      key={c.category}
                      type="button"
                      className="oc-view-tab"
                      onClick={() => setParam('category', c.category)}
                    >
                      {c.label}
                    </button>
                  ))}
              </div>
              {group.map((s) => (
                <SignalRowView key={s.id} signal={s} onChanged={() => void load()} />
              ))}
            </section>
          );
        })}
    </div>
  );
}

function distinctCategories(group: Signal[]): Array<{ category: string; label: string }> {
  const seen = new Map<string, string>();
  for (const s of group) {
    if (!seen.has(s.category)) seen.set(s.category, s.categoryLabel);
  }
  return [...seen].map(([category, label]) => ({ category, label }));
}
